package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text

private val card = Bem("admin-card")

/**
 * The white working area holding one list, form or editor section. Cards never nest.
 *
 * [titleLevel] is 2 inside the shell, where the page header has the `h1`, and 1 where the card is the page.
 *
 * An editor's [saveActions] (Verwerfen, Speichern) stay in view in a sticky footer while its form scrolls: with panes
 * after the [footer]'s actions in one row, on narrow screens alone, with the [footer] as a normal footer before them
 * (SplitView). The [footer] puts an [AdminCardFooterSpacer] after its destructive action.
 */
@Composable
fun AdminCard(
    title: String,
    id: String,
    titleLevel: Int = 2,
    /** Whether only the new website shows what the card holds, which a [BetaBadge] after the title says (#144). */
    beta: Boolean = false,
    header: (@Composable () -> Unit)? = null,
    footer: (@Composable () -> Unit)? = null,
    saveActions: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    val titleId = "$id-title"

    Section({
        classes(card.block)
        id(id)
        attr("aria-labelledby", titleId)
    }) {
        Div({ classes(card.element("header").className) }) {
            val titleAttributes: org.jetbrains.compose.web.attributes.AttrsScope<*>.() -> Unit = {
                classes(card.element("title").className)
                id(titleId)
            }
            val titleContent: @Composable () -> Unit = {
                Text(title)
                if (beta) {
                    BetaBadge()
                }
            }
            if (titleLevel == 1) {
                H1(titleAttributes) { titleContent() }
            } else {
                H2(titleAttributes) { titleContent() }
            }
            header?.invoke()
        }

        Div({ classes(card.element("body").className) }) {
            content()
        }

        val panes = LocalPanes.current
        when {
            saveActions == null -> footer?.let { Div({ classes(card.element("footer").className) }) { it() } }

            panes -> Div({ classes(card.element("footer").className, card.element("footer").modifier("sticky")) }) {
                footer?.invoke() ?: AdminCardFooterSpacer()
                saveActions()
            }

            else -> {
                footer?.let { Div({ classes(card.element("footer").className) }) { it() } }
                Div({ classes(card.element("footer").className, card.element("footer").modifier("sticky")) }) {
                    AdminCardFooterSpacer()
                    saveActions()
                }
            }
        }
    }
}

/** Pushes the following footer actions to the right; the destructive action goes before it. */
@Composable
fun AdminCardFooterSpacer() {
    org.jetbrains.compose.web.dom.Span({ classes(card.element("footer-spacer").className) })
}
